import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Modal } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import axios from 'axios';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FaFilePdf } from 'react-icons/fa';
import excelIcon from '../../assets/icons/excel.png';

// ⬇️ Added for MUI-styled grid/columns
import AppDataGrid from '../../components/common/AppDataGrid';
import { Tooltip } from '@mui/material';
import { Option } from 'lucide-react';

const FeeReport = () => {
  const userData = sessionStorage.getItem('user');
  const userObj = JSON.parse(userData);
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [sortModel, setSortModel] = useState([]);

  const [form, setForm] = useState({
    academic_year_id: userObj.academic_year_id,
    class_id: '',
    section_id: '',
    status: ''
  });

  const [showExportModal, setShowExportModal] = useState(false);
  const [exportType, setExportType] = useState(''); // 'excel' or 'pdf'
  const [selectedColumns, setSelectedColumns] = useState([]);

  const exportableColumns = [
    { field: 'student_id', headerName: 'Student ID' },
    { field: 'admission_number', headerName: 'Admission No' },
    { field: 'student_name', headerName: 'Student Name' },
    { field: 'class_name', headerName: 'Class' },
    { field: 'section_name', headerName: 'Section' },
    { field: 'total_fee', headerName: 'Total Fee' },
    { field: 'total_paid', headerName: 'Total Paid' },
    { field: 'balance', headerName: 'Balance' },
    { field: 'previous_due', headerName: 'Previous Due' },
    { field: 'schedule_amount', headerName: 'Schedule Amount' },
    { field: 'amount_paid_in_schedule', headerName: 'Amount Paid in Schedule' },
    { field: 'amount_to_be_paid', headerName: 'Amount To Be Paid' },
    { field: 'current_schedule_name', headerName: 'Current Schedule' },
    { field: 'current_due_date', headerName: 'Current Due Date' }
  ];

  useEffect(() => {
    fetchAcademicYears();
    fetchClasses();
  }, []);

  useEffect(() => {
    if (form.class_id) fetchSections(form.class_id);
  }, [form.class_id]);

  const fetchAcademicYears = async () => {
    try {
      const res = await axios.post(baseUrl + '/AcademicYear/', {
        action: 'DROPDOWNREAD',
        school_id: userObj.school_id
      });
      setAcademicYears(res.data || []);
    } catch (err) {
      console.error('Error fetching academic years:', err);
    }
  };

  const fetchClasses = async () => {
    try {
      const res = await axios.post(baseUrl + '/classes/', {
        action: 'READ',
        school_id: userObj.school_id
      });
      setClasses(res.data || []);
    } catch (err) {
      console.error('Error fetching classes:', err);
    }
  };

  const fetchSections = async (class_id) => {
    try {
      const res = await axios.post(baseUrl + '/Sections/', {
        action: 'DROPDOWNREAD',
        class_id,
        school_id: userObj.school_id,
        academic_year_id: userObj.academic_year_id,
      });
      setSections(res.data || []);
    } catch (err) {
      console.error('Error fetching sections:', err);
    }
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setForm(prev => ({ ...prev, [id]: value ?? '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const params = {
      _class_id: form.class_id ? parseInt(form.class_id) : null,
      _section_id: form.section_id ? parseInt(form.section_id) : null,
      _academic_year_id: form.academic_year_id ? parseInt(form.academic_year_id) : userObj.academic_year_id,
      _status: form.status || 'A',
      _school_id: userObj.school_id
    };

    try {
      const res = await axios.get(`${baseUrl}/main/teacherappfeeofeverystudnet`, { params });
      const data = res.data?.[0]?.getstudentfeesummarywithdue || [];

      if (data.length === 0) {
        setReportData([]);
        return;
      }

      setReportData(data);
    } catch (err) {
      toast.error("Something went wrong!");
      console.error("Fee Report Submission Error:", err);
    }
  };

  const filteredData = reportData.filter(item =>
    Object.values(item)
      .join(' ')
      .toLowerCase()
      .includes(searchText.toLowerCase())
  );

  const handleExportClick = (type) => {
    if (filteredData.length === 0) {
      toast.error("No data available to export.");
      return;
    }
    setExportType(type);
    setSelectedColumns(exportableColumns.map(col => col.field));
    setShowExportModal(true);
  };

  const handleExportConfirm = () => {
    setShowExportModal(false);
    if (exportType === 'excel') {
      executeExcelExport();
    } else {
      executePdfExport();
    }
  };
  const getSortedData = () => {
    if (!sortModel || sortModel.length === 0) return filteredData;

    const { field, sort } = sortModel[0];

    return [...filteredData].sort((a, b) => {
      let valA = a[field];
      let valB = b[field];

      if (valA === null || valA === undefined) valA = '';
      if (valB === null || valB === undefined) valB = '';

      const numA = Number(valA);
      const numB = Number(valB);

      const isNumericA = valA !== '' && !isNaN(numA);
      const isNumericB = valB !== '' && !isNaN(numB);

      if (isNumericA && isNumericB) {
        return sort === 'asc' ? numA - numB : numB - numA;
      }

      const strA = String(valA).toLowerCase();
      const strB = String(valB).toLowerCase();

      if (strA < strB) return sort === 'asc' ? -1 : 1;
      if (strA > strB) return sort === 'asc' ? 1 : -1;
      return 0;
    });
  };
  const handleColumnToggle = (field) => {
    setSelectedColumns(prev =>
      prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field]
    );
  };

  const executeExcelExport = () => {
  const sortedData = getSortedData();

  const activeCols = exportableColumns.filter(c => selectedColumns.includes(c.field));

  const formatted = sortedData.map(row => {
    let newRow = {};
    activeCols.forEach(c => {
      newRow[c.headerName] = row[c.field];
    });
    return newRow;
  });

  const ws = XLSX.utils.json_to_sheet(formatted);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "FeeReport");
  XLSX.writeFile(wb, "Fee_Report.xlsx");
};
  const executePdfExport = () => {
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text("Fee Report", 14, 15);

    const activeCols = exportableColumns.filter(c => selectedColumns.includes(c.field));
    const columnsForPdf = activeCols.map(c => c.headerName);
const sortedData = getSortedData();


    const rows = sortedData.map(row => {
      return activeCols.map(c => {
        let val = row[c.field];
        if (val == null) {
          return (['total_fee', 'total_paid', 'balance', 'previous_due', 'schedule_amount', 'amount_paid_in_schedule', 'amount_to_be_paid'].includes(c.field)) ? 0 : '';
        }
        return val;
      });
    });

    autoTable(doc, {
      startY: 20,
      head: [columnsForPdf],
      body: rows,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [41, 128, 185] },
    });

    doc.save("Fee_Report.pdf");
  };

  // ========== MUI DataGrid columns (matching your preferred style) ==========
  const formatCurrency = (v) =>
    typeof v === 'number' ? `₹ ${v.toLocaleString()}` : (v ? `₹ ${Number(v).toLocaleString()}` : '₹ 0');

  const columns = [
    {
      field: 'student_id',
      headerName: 'Student ID',
      flex: 1,

      headerAlign: 'center',
      align: 'center',
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value ?? ''}>
          <span>{params.value ?? ''}</span>
        </Tooltip>
      ),
    },
    {
      field: 'admission_number',
      headerName: 'Adm No',
      flex: 1,

      headerAlign: 'center',
      align: 'center',
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value ?? ''}>
          <span>{params.value ?? ''}</span>
        </Tooltip>
      ),
    },
    {
      field: 'student_name',
      headerName: 'Student Name',
      flex: 1,

      headerAlign: 'center',
      align: 'center',
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value ?? ''}>
          <span style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
            {params.value ?? ''}
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'class_name',
      headerName: 'Class',
      flex: 1,

      headerAlign: 'center',
      align: 'center',
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value ?? ''}>
          <span>{params.value ?? ''}</span>
        </Tooltip>
      ),
    },
    {
      field: 'section_name',
      headerName: 'Section',
      flex: 1,

      headerAlign: 'center',
      align: 'center',
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value ?? ''}>
          <span>{params.value ?? ''}</span>
        </Tooltip>
      ),
    },
    {
      field: 'total_fee',
      headerName: 'Total Fee',
      flex: 1,

      headerAlign: 'center',
      align: 'right',
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={formatCurrency(params.value)}>
          <span>{formatCurrency(params.value)}</span>
        </Tooltip>
      ),
    },
    {
      field: 'total_paid',
      headerName: 'Total Paid',
      flex: 1,

      headerAlign: 'center',
      align: 'right',
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={formatCurrency(params.value)}>
          <span>{formatCurrency(params.value)}</span>
        </Tooltip>
      ),
    },
    {
      field: 'balance',
      headerName: 'Balance',
      flex: 1,

      headerAlign: 'center',
      align: 'right',
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={formatCurrency(params.value)}>
          <span>{formatCurrency(params.value)}</span>
        </Tooltip>
      ),
    },
    {
      field: 'previous_due',
      headerName: 'Previous Due',
      flex: 1,

      headerAlign: 'center',
      align: 'right',
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={formatCurrency(params.value)}>
          <span>{formatCurrency(params.value)}</span>
        </Tooltip>
      ),
    },
    {
      field: 'schedule_amount',
      headerName: 'Schedule Amt',
      flex: 1,

      headerAlign: 'center',
      align: 'right',
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={formatCurrency(params.value)}>
          <span>{formatCurrency(params.value)}</span>
        </Tooltip>
      ),
    },
    {
      field: 'amount_paid_in_schedule',
      headerName: 'Schedule Amt Paid',
      flex: 1,

      headerAlign: 'center',
      align: 'right',
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={formatCurrency(params.value)}>
          <span>{formatCurrency(params.value)}</span>
        </Tooltip>
      ),
    },
    {
      field: 'amount_to_be_paid',
      headerName: 'Amt To Be Paid',
      flex: 1,

      headerAlign: 'center',
      align: 'right',
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={formatCurrency(params.value)}>
          <span>{formatCurrency(params.value)}</span>
        </Tooltip>
      ),
    },
    {
      field: 'current_schedule_name',
      headerName: 'Cur Schedule',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value ?? ''}>
          <span style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
            {params.value ?? ''}
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'current_due_date',
      headerName: 'Cur DueDate',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value ?? ''}>
          <span>{params.value ?? ''}</span>
        </Tooltip>
      ),
    },
  ];

  // Unique row id for the grid (robust fallback)
  const getRowId = (row) =>
    row.id ??
    row.student_id ??
    `${row.admission_number || ''}-${row.class_name || ''}-${row.section_name || ''}`;

  return (
    <div className='pageBody'>
      <ToastContainer />
      <Container fluid>
        <Card>
          <Card.Body className="hide-scrollbar">
            <form onSubmit={handleSubmit}>
              <Row>
                <Col xs={12}>
                  <h6 className='commonSectionTitle'>Fee Report</h6>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6} lg={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Academic Year</Form.Label>
                      <Form.Select id='academic_year_id' value={form.academic_year_id} onChange={handleInputChange}>
                        <option>Select Academic Year</option>
                        {academicYears.map(year => (
                          <option key={year.academic_year_id} value={year.academic_year_id}>{year.academic_year_name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                </Col>
                <Col xs={12} md={6} lg={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Class</Form.Label>
                      <Form.Select id='class_id' value={form.class_id} onChange={handleInputChange}>
                        <option>Select Class</option>
                        {classes.map(cls => (
                          <option key={cls.class_id} value={cls.class_id}>{cls.class_name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                </Col>
                <Col xs={12} md={6} lg={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Section</Form.Label>
                      <Form.Select id='section_id' value={form.section_id} onChange={handleInputChange}>
                        <option>Select Section</option>
                        {sections.map(sec => (
                          <option key={sec.section_id} value={sec.section_id}>{sec.section_name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                </Col>
                <Col xs={12} md={6} lg={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Status</Form.Label>
                      <Form.Select id='status' value={form.status} onChange={handleInputChange}>
                        <option value='' disabled hidden>Select Status</option>
                        <option value='A'>All</option>
                        <option value='P'>Paid</option>
                        <option value='N'>Not Paid</option>
                      </Form.Select>
                    </Form.Group>
                  </div>
                </Col>
                <Col>
                  <div className="d-flex justify-content-end mt-4">
                    <Button
                      type="button"
                      variant="primary"
                      className="btn-info clearBtn me-3"
                      onClick={() => setForm({ academic_year_id: userObj.academic_year_id, class_id: '', section_id: '', status: '' })}
                    >
                      Reset
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      className="btn-success primaryBtn"
                    >
                      Search
                    </Button>
                  </div>
                </Col>
              </Row>
            </form>
          </Card.Body>
        </Card>

        <Row className='mt-3 mb-2 align-items-center'>
          <Col md={3}>
            <Form.Control
              type="text"
              placeholder="Search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Col>
          <Col className="d-flex justify-content-end">
            <Button variant="success" onClick={() => handleExportClick('excel')} className="me-2">
              <img src={excelIcon} alt="excel" style={{ width: 20, marginRight: 5 }} />
            </Button>
            <Button variant="danger" onClick={() => handleExportClick('pdf')}>
              <FaFilePdf style={{ marginRight: 5 }} />
            </Button>
          </Col>
        </Row>

        <div className="commonTable mt-2">
          <AppDataGrid
            rows={filteredData}
            columns={columns}
            sortModel={sortModel}
            onSortModelChange={(model) => setSortModel(model)}
            getRowId={getRowId}
            pageSizeOptions={[15, 25, 50]}
            showToolbar={false}
            pagination
            density="compact"
          />
        </div>
      </Container>

      <Modal show={showExportModal} onHide={() => setShowExportModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Select Columns to Export</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {exportableColumns.map(col => (
              <Form.Check
                key={col.field}
                type="checkbox"
                id={`export-col-${col.field}`}
                label={col.headerName}
                checked={selectedColumns.includes(col.field)}
                onChange={() => handleColumnToggle(col.field)}
                className="mb-2"
              />
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowExportModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleExportConfirm} disabled={selectedColumns.length === 0}>
            Export
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default FeeReport;
